import { Alert, Box, Stack } from "@mui/material";
import { useCallback } from "react";

import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import AppIcon from "@/common/components/Icons/AppIcon";
import ConfirmationModalWithTrigger from "@/common/components/Modals/ConfirmationModalWithTrigger";
import { TenantRequestHelper } from "@/common/helpers/entity/tenantRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  TenantRequestDto,
  TenantRequestResponseType,
  TenantRequestStage,
} from "@/core/api/generated/v1";

import AuthorizedElement from "../../Auth/AuthorizedElement";
import AppButton from "../../Button/AppButton";
import AppIconButton from "../../Button/AppIconButton";
import ChatActivator from "../Chat/ChatActivator";
import TenantRequestApplyResultControl from "./TenantRequestApplyResultControl";

interface Props {
  tenantRequest: TenantRequestDto;
  /** Either normal or icon buttons are rendered. */
  variant?: "normal" | "compact";
  onUpdate: (newValue: TenantRequestDto) => void;
}

/** Primary actions for controlling TenantRequest stage. */
export default function TenantRequestStagePrimaryActions({
  tenantRequest,
  variant = "normal",
  onUpdate,
}: Props) {
  //
  const currentTenant = useCurrentTenant();
  const { enqueueSnackbar } = useAppSnackbar();

  const handleSend = useCallback(async () => {
    if (!tenantRequest) {
      return;
    }
    try {
      const response =
        await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdStageSendPost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            tenantRequestId: tenantRequest.id!,
          },
        );
      enqueueSnackbar("Request was sent. Please wait for the answer.", { variant: "success" });
      onUpdate(response.data);
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    }
  }, [tenantRequest]);

  const handleRespond = useCallback(
    async (responseType: TenantRequestResponseType, reason?: string) => {
      if (!tenantRequest) {
        return;
      }
      try {
        const response =
          await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdStageRespondPost(
            {
              nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
              tenantRequestId: tenantRequest.id!,
              tenantRequestRespondDto: {
                responseType: responseType,
                reason: reason,
              },
            },
          );

        if (responseType === TenantRequestResponseType.Accept) {
          enqueueSnackbar(
            "Your response was successfully saved. You can now start working on the request.",
            { variant: "success" },
          );
        } else {
          enqueueSnackbar("Your response was successfully saved.", { variant: "success" });
        }

        onUpdate(response.data);
      } catch (err) {
        const validation2 = ValidationHelper.handleApiErrorResponse(err);
        validation2.hasErrors &&
          enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
      }
    },
    [tenantRequest],
  );

  const handleMoveToDraft = useCallback(async () => {
    if (!tenantRequest) {
      return;
    }
    try {
      const response =
        await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdStageDraftPost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            tenantRequestId: tenantRequest.id!,
          },
        );
      enqueueSnackbar("Request was moved to draft.", { variant: "success" });
      onUpdate(response.data);
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    }
  }, [tenantRequest]);

  const handleResend = useCallback(async () => {
    await handleSend();
  }, [handleSend]);

  const handleStart = useCallback(async () => {
    if (!tenantRequest) {
      return;
    }
    try {
      const response =
        await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdStageStartPost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            tenantRequestId: tenantRequest.id!,
          },
        );
      enqueueSnackbar(
        "Request was started When you are done, please complete it and report back.",
        { variant: "success" },
      );
      onUpdate(response.data);
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    }
  }, [tenantRequest]);

  const handleAcceptAndStart = useCallback(
    async (reason?: string) => {
      await handleRespond(TenantRequestResponseType.Accept, reason);
      await handleStart();
    },
    [handleRespond, handleStart],
  );

  const handleComplete = useCallback(async () => {
    if (!tenantRequest) {
      return;
    }
    try {
      const response =
        await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdStageCompletePost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            tenantRequestId: tenantRequest.id!,
          },
        );
      enqueueSnackbar("Request completed. Now, you need to report results back to the sender.", {
        variant: "success",
      });
      onUpdate(response.data);
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    }
  }, [tenantRequest]);

  const handleReport = useCallback(async () => {
    if (!tenantRequest) {
      return;
    }
    try {
      const response =
        await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdStageReportPost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            tenantRequestId: tenantRequest.id!,
          },
        );
      enqueueSnackbar("Request reported. The sender needs to review the results.", {
        variant: "success",
      });
      onUpdate(response.data);
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    }
  }, [tenantRequest]);

  const handleCompleteAndReport = useCallback(async () => {
    await handleComplete();
    await handleReport();
  }, [handleComplete, handleReport]);

  const handleReturn = useCallback(
    async (reason?: string) => {
      if (!tenantRequest) {
        return;
      }
      try {
        const response =
          await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdStageReturnPost(
            {
              nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
              tenantRequestId: tenantRequest.id!,
              tenantRequestReturnDto: {
                reason: reason || undefined,
              },
            },
          );
        enqueueSnackbar("Request was returned to the sender.", { variant: "success" });
        onUpdate(response.data);
      } catch (err) {
        const validation2 = ValidationHelper.handleApiErrorResponse(err);
        validation2.hasErrors &&
          enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
      }
    },
    [tenantRequest],
  );

  const handleRework = useCallback(
    async (reason?: string) => {
      if (!tenantRequest) {
        return;
      }
      try {
        const response =
          await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdStageReworkPost(
            {
              nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
              tenantRequestId: tenantRequest.id!,
              tenantRequestReworkDto: {
                reason: reason,
              },
            },
          );
        enqueueSnackbar(
          "Request returned on rework. The sender needs to revise the result and report again.",
          {
            variant: "success",
          },
        );
        onUpdate(response.data);
      } catch (err) {
        const validation2 = ValidationHelper.handleApiErrorResponse(err);
        validation2.hasErrors &&
          enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
      }
    },
    [tenantRequest],
  );

  const handleClose = useCallback(
    async (reason?: string) => {
      if (!tenantRequest) {
        return;
      }
      try {
        const response =
          await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdStageClosePost(
            {
              nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
              tenantRequestId: tenantRequest.id!,
              tenantRequestCloseDto: {
                reason: reason,
              },
            },
          );
        enqueueSnackbar("Request was closed. You can apply the result.", { variant: "success" });
        onUpdate(response.data);
      } catch (err) {
        const validation2 = ValidationHelper.handleApiErrorResponse(err);
        validation2.hasErrors &&
          enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
      }
    },
    [tenantRequest],
  );

  const handleApplyResult = useCallback(async () => {
    if (!tenantRequest) {
      return;
    }
    try {
      const response =
        await apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsTenantRequestIdResultApplyPost(
          {
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            tenantRequestId: tenantRequest.id!,
            tenantRequestApplyResultDto: {
              isReapply: false,
            },
          },
        );
      enqueueSnackbar("The result was successfully applied. No more actions needed.", {
        variant: "success",
      });
      onUpdate(response.data);
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    }
  }, [tenantRequest]);

  const handleCloseAndApplyResult = useCallback(
    async (reason?: string) => {
      await handleClose(reason);
      await handleApplyResult();
    },
    [handleClose, handleApplyResult],
  );

  return (
    <AuthorizedElement permissions={[AppPermission.TenantRequestManage]}>
      <Stack direction='row' spacing={1} alignItems='center'>
        {/* Send */}
        {TenantRequestHelper.isSentByMe(currentTenant, tenantRequest) &&
          tenantRequest?.stageHistory?.stage === TenantRequestStage.Draft && (
            <ConfirmationModalWithTrigger
              trigger={
                variant === "normal" ? (
                  <AppButton variant='contained' color='primary' startIcon={<AppIcon of='send' />}>
                    Send
                  </AppButton>
                ) : (
                  <AppIconButton
                    variant='outlined'
                    color='primary'
                    size='small'
                    tooltipProps={{ title: "Send" }}
                  >
                    <AppIcon of='send' />
                  </AppIconButton>
                )
              }
              title={`Send the request to company '${tenantRequest?.receiverTenant?.name}'?`}
              body={`You won't be able to edit the request once it's sent.`}
              onConfirm={handleSend}
            />
          )}

        {/* Respond */}
        {TenantRequestHelper.isReceivedByMe(currentTenant, tenantRequest) &&
          tenantRequest?.stageHistory?.stage === TenantRequestStage.Pending && (
            <>
              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='success'
                      startIcon={<AppIcon of='start' />}
                    >
                      Accept & start
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='success'
                      size='small'
                      tooltipProps={{ title: "Accept & start" }}
                    >
                      <AppIcon of='start' />
                    </AppIconButton>
                  )
                }
                title={`Accept and start the request?`}
                body={<>You accept the request.</>}
                withReasonText
                onConfirm={(params) => handleAcceptAndStart(params.reasonText)}
              />

              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='success'
                      startIcon={<AppIcon of='accept' />}
                    >
                      Accept
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='success'
                      size='small'
                      tooltipProps={{ title: "Accept" }}
                    >
                      <AppIcon of='accept' />
                    </AppIconButton>
                  )
                }
                title={`Accept the request?`}
                body={<>You accept the request.</>}
                withReasonText
                onConfirm={(params) =>
                  handleRespond(TenantRequestResponseType.Accept, params.reasonText)
                }
              />

              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='error'
                      startIcon={<AppIcon of='decline' />}
                    >
                      Decline
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='error'
                      size='small'
                      tooltipProps={{ title: "Decline" }}
                    >
                      <AppIcon of='decline' />
                    </AppIconButton>
                  )
                }
                title={`Decline the request?`}
                body={<>You decline the request.</>}
                withReasonText
                onConfirm={(params) =>
                  handleRespond(TenantRequestResponseType.Decline, params.reasonText)
                }
              />
            </>
          )}

        {/* Move to draft */}
        {TenantRequestHelper.isSentByMe(currentTenant, tenantRequest) &&
          (tenantRequest?.stageHistory?.stage === TenantRequestStage.Declined ||
            tenantRequest?.stageHistory?.stage === TenantRequestStage.Returned) && (
            <>
              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='primary'
                      startIcon={<AppIcon of='edit' />}
                    >
                      Move to draft
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='primary'
                      size='small'
                      tooltipProps={{ title: "Move to draft" }}
                    >
                      <AppIcon of='edit' />
                    </AppIconButton>
                  )
                }
                title={`Move the request back to draft?`}
                body={
                  <>
                    The request will be moved to{" "}
                    <strong>
                      <InlineApiEnumValue
                        type='TenantRequestStage'
                        value={TenantRequestStage.Draft}
                      />
                    </strong>
                    .
                  </>
                }
                onConfirm={handleMoveToDraft}
              />
            </>
          )}

        {/* Resend */}
        {TenantRequestHelper.isSentByMe(currentTenant, tenantRequest) &&
          (tenantRequest?.stageHistory?.stage === TenantRequestStage.Declined ||
            tenantRequest?.stageHistory?.stage === TenantRequestStage.Returned) && (
            <>
              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='primary'
                      startIcon={<AppIcon of='send' />}
                    >
                      Resend
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='primary'
                      size='small'
                      tooltipProps={{ title: "Resend" }}
                    >
                      <AppIcon of='send' />
                    </AppIconButton>
                  )
                }
                title={`Resend the request to company '${tenantRequest?.receiverTenant?.name}'?`}
                body={`Resend the declined or returned request without any edits. You won't be able to edit the request once it's sent.`}
                onConfirm={handleResend}
              />
            </>
          )}

        {/* Start */}
        {TenantRequestHelper.isReceivedByMe(currentTenant, tenantRequest) &&
          tenantRequest?.stageHistory?.stage === TenantRequestStage.Accepted && (
            <ConfirmationModalWithTrigger
              trigger={
                variant === "normal" ? (
                  <AppButton variant='contained' color='primary' startIcon={<AppIcon of='start' />}>
                    Start
                  </AppButton>
                ) : (
                  <AppIconButton
                    variant='outlined'
                    color='primary'
                    size='small'
                    tooltipProps={{ title: "Start" }}
                  >
                    <AppIcon of='start' />
                  </AppIconButton>
                )
              }
              title={`Start working on the request?`}
              body={
                <>
                  The request will be moved to{" "}
                  <strong>
                    <InlineApiEnumValue
                      type='TenantRequestStage'
                      value={TenantRequestStage.InProgress}
                    />
                  </strong>
                  .
                </>
              }
              onConfirm={handleStart}
            />
          )}

        {/* Complete */}
        {TenantRequestHelper.isReceivedByMe(currentTenant, tenantRequest) &&
          tenantRequest?.stageHistory?.stage === TenantRequestStage.InProgress && (
            <>
              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='primary'
                      startIcon={<AppIcon of='report' />}
                    >
                      Complete & report
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='primary'
                      size='small'
                      tooltipProps={{ title: "Complete & report" }}
                    >
                      <AppIcon of='report' />
                    </AppIconButton>
                  )
                }
                title={`Complete and report result back to the sender?`}
                body={
                  <Stack gap={1}>
                    <Box>
                      The request will be moved to{" "}
                      <strong>
                        <InlineApiEnumValue
                          type='TenantRequestStage'
                          value={TenantRequestStage.Reporting}
                        />
                      </strong>
                      .
                    </Box>

                    {!tenantRequest?.result && (
                      <Alert severity='warning'>
                        No result added! Please add result before reporting.
                      </Alert>
                    )}
                  </Stack>
                }
                onConfirm={handleCompleteAndReport}
              />

              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='primary'
                      startIcon={<AppIcon of='complete' />}
                    >
                      Complete
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='primary'
                      size='small'
                      tooltipProps={{ title: "Complete" }}
                    >
                      <AppIcon of='complete' />
                    </AppIconButton>
                  )
                }
                title={`Complete the request?`}
                body={
                  <>
                    This will move it to{" "}
                    <strong>
                      <InlineApiEnumValue
                        type='TenantRequestStage'
                        value={TenantRequestStage.Completed}
                      />
                    </strong>
                    .
                  </>
                }
                onConfirm={handleComplete}
              />
            </>
          )}

        {/* Report */}
        {TenantRequestHelper.isReceivedByMe(currentTenant, tenantRequest) &&
          tenantRequest?.stageHistory?.stage === TenantRequestStage.Completed && (
            <ConfirmationModalWithTrigger
              trigger={
                variant === "normal" ? (
                  <AppButton
                    variant='contained'
                    color='primary'
                    startIcon={<AppIcon of='report' />}
                  >
                    Report
                  </AppButton>
                ) : (
                  <AppIconButton
                    variant='outlined'
                    color='primary'
                    size='small'
                    tooltipProps={{ title: "Report" }}
                  >
                    <AppIcon of='report' />
                  </AppIconButton>
                )
              }
              title={`Report result back to the sender?`}
              body={
                <Stack gap={1}>
                  <Box>
                    The request will be moved to{" "}
                    <strong>
                      <InlineApiEnumValue
                        type='TenantRequestStage'
                        value={TenantRequestStage.Reporting}
                      />
                    </strong>
                    .
                  </Box>

                  {!tenantRequest?.result && (
                    <Alert severity='warning'>
                      No result added! Please add result before reporting.
                    </Alert>
                  )}
                </Stack>
              }
              onConfirm={handleReport}
            />
          )}

        {/* Return */}
        {TenantRequestHelper.isReceivedByMe(currentTenant, tenantRequest) &&
          (tenantRequest?.stageHistory?.stage === TenantRequestStage.Accepted ||
            tenantRequest?.stageHistory?.stage === TenantRequestStage.InProgress ||
            tenantRequest?.stageHistory?.stage === TenantRequestStage.Completed) && (
            <>
              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='primary'
                      startIcon={<AppIcon of='return' />}
                    >
                      Return
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='primary'
                      size='small'
                      tooltipProps={{ title: "Return to the sender" }}
                    >
                      <AppIcon of='return' />
                    </AppIconButton>
                  )
                }
                title={`Return the request back to the sender?`}
                body={`This will delete the result you might have added to the request.`}
                withReasonText
                onConfirm={(params) => handleReturn(params.reasonText)}
              />
            </>
          )}

        {/* Close */}
        {TenantRequestHelper.isSentByMe(currentTenant, tenantRequest) &&
          tenantRequest?.stageHistory?.stage === TenantRequestStage.Reporting && (
            <>
              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='success'
                      startIcon={<AppIcon of='accept' />}
                    >
                      Accept & apply result
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='success'
                      size='small'
                      tooltipProps={{ title: "Accept & apply the result" }}
                    >
                      <AppIcon of='apply' />
                    </AppIconButton>
                  )
                }
                title={`Accept and apply the result?`}
                body={
                  <>
                    You accept and apply the result to your company. The request will be moved to{" "}
                    <strong>
                      <InlineApiEnumValue
                        type='TenantRequestStage'
                        value={TenantRequestStage.Closed}
                      />
                    </strong>
                    .
                  </>
                }
                withReasonText
                onConfirm={(params) => handleCloseAndApplyResult(params.reasonText)}
              />

              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='success'
                      startIcon={<AppIcon of='accept' />}
                    >
                      Accept result
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='success'
                      size='small'
                      tooltipProps={{ title: "Accept result & close" }}
                    >
                      <AppIcon of='accept' />
                    </AppIconButton>
                  )
                }
                title={`Accept result and close the request?`}
                body={
                  <>
                    You accept the result. The request will be moved to{" "}
                    <strong>
                      <InlineApiEnumValue
                        type='TenantRequestStage'
                        value={TenantRequestStage.Closed}
                      />
                    </strong>
                    .
                  </>
                }
                withReasonText
                onConfirm={(params) => handleClose(params.reasonText)}
              />

              <ConfirmationModalWithTrigger
                trigger={
                  variant === "normal" ? (
                    <AppButton
                      variant='contained'
                      color='error'
                      startIcon={<AppIcon of='decline' />}
                    >
                      Decline result
                    </AppButton>
                  ) : (
                    <AppIconButton
                      variant='outlined'
                      color='error'
                      size='small'
                      tooltipProps={{ title: "Decline result & send on rework" }}
                    >
                      <AppIcon of='decline' />
                    </AppIconButton>
                  )
                }
                title={`Decline result and send on rework?`}
                body={
                  <>
                    You decline the result. The request will be returned on rework to the receiver.
                    This will move it to{" "}
                    <strong>
                      <InlineApiEnumValue
                        type='TenantRequestStage'
                        value={TenantRequestStage.InProgress}
                      />
                    </strong>
                    .
                  </>
                }
                withReasonText
                onConfirm={(params) => handleRework(params.reasonText)}
              />
            </>
          )}

        {/* Apply result */}
        {TenantRequestHelper.isSentByMe(currentTenant, tenantRequest) &&
          tenantRequest?.stageHistory?.stage === TenantRequestStage.Closed && (
            <TenantRequestApplyResultControl
              tenantRequest={tenantRequest}
              variant={variant}
              isForOperation
              buttonProps={{
                size: variant === "normal" ? "medium" : "small",
              }}
              onEntityUpdate={(newValue) => {
                onUpdate(newValue);
              }}
            />
          )}

        {/* Chat */}
        {tenantRequest?.chat && (
          <ChatActivator
            chatType={tenantRequest.chat.type!}
            chatId={tenantRequest.chat.id}
            extraContext={{
              tenantToTenant: {
                tenant1Id: tenantRequest.senderTenant?.id || "",
                tenant2Id: tenantRequest.receiverTenant?.id || "",
              },
            }}
            chatPlacement='popover'
            to={undefined}
            withActivityIndicator
            tooltipTitle='Open C2C chat'
            activatorButtonProps={{
              disableRipple: true,
            }}
          />
        )}
      </Stack>
    </AuthorizedElement>
  );
}
